import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarMenu from "../Administrador/AdminPanel/SidebarMenu";
import TopBar from "../Administrador/AdminPanel/TopBar";
import InfoPurchase from "./RegisterPurchase/InfoPurchase";
import PurchaseDetail from "./RegisterPurchase/PurchaseDetail";

const getCSRFToken = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();

    if (cookie.startsWith(name + "=")) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }

  return null;
};

const NewPurchase = () => {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [purchaseInfo, setPurchaseInfo] = useState({});
  const [purchaseData, setPurchaseData] = useState({});
  const [productRows, setProductRows] = useState([]);
  const [productDescripcion, setProductDescripcion] = useState("");
  const [accountRows, setAccountRows] = useState([]);
  const [payments, setPayments] = useState([]);

  const [saving, setSaving] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackError, setSnackError] = useState("");

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Documento registrado correctamente");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isSuper = localStorage.getItem("is_superuser") === "true";
    const isAdmin = localStorage.getItem("is_admin") === "true";
    const isVendedor = localStorage.getItem("is_vendedor") === "true";

    if (!isSuper && !isAdmin && !isVendedor) {
      navigate("/login");
    }
  }, [location, navigate]);

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  const handleInfoChange = (updater) => {
    setPurchaseInfo((prev) => {
      const base = prev || {};
      const next = typeof updater === "function" ? updater(base) : updater;
      const merged = { ...base, ...(next || {}) };

      if (merged?.tipo_documento) {
        setPurchaseData((p) => ({
          ...(p || {}),
          tipo_documento: merged.tipo_documento,
        }));
      }

      if (merged?.inventario?.tipo) {
        setPurchaseData((p) => ({
          ...(p || {}),
          inventario_tipo: merged.inventario.tipo,
        }));
      }

      return merged;
    });
  };

  const handleCloseSuccess = () => {
    setSuccessOpen(false);
    navigate("/admin-panel/reporte/documento");
  };

  const handleSavePurchase = async () => {
    if (saving) return;

    setSaving(true);

    const tipoDoc = purchaseInfo?.tipo_documento || purchaseData?.tipo_documento || "FACTURA";
    const bodegaId = purchaseData?.bodega_id || purchaseInfo?.bodega_id || "";

    const productosPayload = Array.isArray(purchaseData?.productos)
      ? purchaseData.productos
      : productRows;

    const cuentasPayload = Array.isArray(purchaseData?.cuentas)
      ? purchaseData.cuentas
      : accountRows;

    if (tipoDoc === "INVENTARIO") {
      const inv = purchaseInfo?.inventario || {};
      const inventarioTipo = (inv.tipo || purchaseData?.inventario_tipo || "INGRESO").toUpperCase();
      const items = purchaseData?.items || [];

      const payload = {
        tipo: inventarioTipo,
        fecha: inv.fecha || "",
        referencia: inv.referencia || "",
        descripcion: inv.descripcion || "",
        bodega_id: bodegaId,
        bodega: bodegaId,
        aplicar: true,
        items: items.map((item) => ({
          ...item,
          bodega_id: item.bodega_id || bodegaId,
          bodega: item.bodega || item.bodega_id || bodegaId,
        })),
      };

      try {
        const res = await fetch("/inventario/registrar/", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(json.error || "Error al registrar inventario");
        }

        setSuccessMsg("Movimiento de inventario registrado correctamente");
        setSuccessOpen(true);
      } catch (err) {
        setSnackError(err.message);
        setSnackOpen(true);
      } finally {
        setSaving(false);
      }

      return;
    }

    const descripcionGlobal =
      purchaseData?.descripcion_global ||
      productDescripcion ||
      purchaseInfo?.descripcion ||
      "";

    const infoCompra = {
      ...purchaseInfo,
      bodega_id: bodegaId,
      bodega: bodegaId,
    };

    const payload = {
      info_compra: infoCompra,
      datos_compra: {
        ...purchaseData,
        bodega_id: bodegaId,
        bodega: bodegaId,
        descripcion: descripcionGlobal,
      },
      productos: productosPayload.map((row) => ({
        ...row,
        bodega_id: row.bodega_id || bodegaId,
        bodega: row.bodega || row.bodega_id || bodegaId,
      })),
      cuentas: cuentasPayload,
      gastos: cuentasPayload,
      descripcion: descripcionGlobal,
      pagos: payments,
      bodega_id: bodegaId,
      bodega: bodegaId,
      sin_movimiento_inventario: purchaseData?.sin_movimiento_inventario || false,
    };

    try {
      const res = await fetch("/documentos/compras/registrar/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.error || "Error al registrar compra");
      }

      setSuccessMsg("Compra registrada correctamente");
      setSuccessOpen(true);
    } catch (err) {
      setSnackError(err.message);
      setSnackOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", background: "#f5f6fa", minHeight: "100vh" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1565C0", mb: 2 }}>
              Registrar Compra
            </Typography>

            <InfoPurchase onChange={handleInfoChange} />

            <PurchaseDetail
              purchaseData={purchaseData}
              onPurchaseDataChange={setPurchaseData}
              payments={payments}
              setPayments={setPayments}
              productRows={productRows}
              setProductRows={setProductRows}
              productDescripcion={productDescripcion}
              setProductDescripcion={setProductDescripcion}
              accountRows={accountRows}
              setAccountRows={setAccountRows}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="contained"
                sx={{ bgcolor: "#1565C0" }}
                onClick={handleSavePurchase}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog open={successOpen} onClose={handleCloseSuccess}>
        <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2, px: 5 }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 72, color: "#2E7D32" }} />
          <Typography sx={{ fontWeight: 900, fontSize: 18, mt: 1 }}>
            Guardado con éxito
          </Typography>
          <Typography sx={{ color: "#757575", mt: 0.5 }}>
            {successMsg}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            sx={{ textTransform: "none", bgcolor: "#1565C0", px: 4 }}
            onClick={handleCloseSuccess}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewPurchase;